import { BaseService } from './BaseService.js';

/**
 * Booking Service
 * Handles all booking-related operations using Laravel API
 */
export class BookingService extends BaseService {
  constructor() {
    super('bookings'); // Laravel endpoint: /api/bookings
  }

  /**
   * Create new booking with validation
   */
  async createBooking(bookingData) {
    // Validate booking data before creating
    const validation = this.validateBookingData(bookingData);
    if (!validation.isValid) {
      throw new Error(`Booking validation failed: ${validation.errors.join(', ')}`);
    }

    return this.create(bookingData);
  }

  /**
   * Get bookings by status
   */
  async getByStatus(status) {
    return this.list(1, 50, { status });
  }

  /**
   * Get bookings by date range
   */
  async getByDateRange(startDate, endDate) {
    return this.customCall('date-range', 'GET', {
      start_date: startDate,
      end_date: endDate
    });
  }

  /**
   * Get customer bookings
   */
  async getCustomerBookings(customerId) {
    return this.list(1, 50, { customer_id: customerId });
  }

  /**
   * Get platform bookings
   */
  async getPlatformBookings(platformId) {
    return this.list(1, 50, { platform_id: platformId });
  }

  /**
   * Confirm booking
   */
  async confirmBooking(bookingId) {
    return this.update(bookingId, { status: 'bestätigt' });
  }

  /**
   * Cancel booking
   */
  async cancelBooking(bookingId, reason = null) {
    return this.update(bookingId, { 
      status: 'storniert',
      cancellation_reason: reason
    });
  }

  /**
   * Calculate booking price
   */
  async calculatePrice(bookingData) {
    return this.customCall('calculate-price', 'POST', bookingData);
  }

  /**
   * Get booking statistics
   */
  async getStatistics(dateRange = null) {
    const params = dateRange ? { date_range: dateRange } : {};
    return this.customCall('statistics', 'GET', params);
  }

  /**
   * Get upcoming bookings
   */
  async getUpcoming() {
    return this.customCall('upcoming');
  }

  /**
   * Get overdue bookings
   */
  async getOverdue() {
    return this.customCall('overdue');
  }

  /**
   * Update booking status
   */
  async updateStatus(bookingId, newStatus, notes = null) {
    return this.update(bookingId, {
      status: newStatus,
      status_notes: notes
    });
  }

  /**
   * Add payment to booking
   */
  async addPayment(bookingId, paymentData) {
    return this.customCall(`${bookingId}/payment`, 'POST', paymentData);
  }

  /**
   * Generate booking contract
   */
  async generateContract(bookingId) {
    return this.customCall(`${bookingId}/contract`, 'POST');
  }

  /**
   * Send booking confirmation email
   */
  async sendConfirmationEmail(bookingId) {
    return this.customCall(`${bookingId}/send-confirmation`, 'POST');
  }

  /**
   * Validate booking data
   */
  validateBookingData(data) {
    const errors = [];
    
    if (!data.customer_id) {
      errors.push('Customer ID ist erforderlich');
    }
    
    if (!data.platform_id) {
      errors.push('Platform ID ist erforderlich');
    }
    
    if (!data.start_date) {
      errors.push('Startdatum ist erforderlich');
    }
    
    if (!data.end_date) {
      errors.push('Enddatum ist erforderlich');
    }
    
    if (data.start_date && data.end_date) {
      const start = new Date(data.start_date);
      const end = new Date(data.end_date);
      
      if (start >= end) {
        errors.push('Enddatum muss nach dem Startdatum liegen');
      }
      
      if (start < new Date()) {
        errors.push('Startdatum kann nicht in der Vergangenheit liegen');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
} 